import MasqueradeActiveSession from '@components/MasqueradeActiveSession';
import { useAuth } from '@contexts/AuthContext';
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import SubscriptionRequiredModal from '@components/SubscriptionRequiredModal';
import WelcomeModal from '@pages/Home/components/Objectives/WelcomeModal';
import { useAppSelectors, useAppStore } from '@stores/app/appStore';
import { useEffect, useRef, useState } from 'react';
import type { Subscription } from '@customTypes/billing';

// Whether a subscription should be treated as inactive for paywall purposes:
// absent, explicitly canceled, or a scheduled-to-cancel sub whose end date has
// already passed. The last case catches records the Stripe cancel webhook left
// stale at 'trialing'/'active' (the deleted event historically skipped them), so
// the paywall still fires off the source of truth without rewriting it. Gating on
// cancel_at_period_end avoids paywalling an auto-renewing sub whose end_at is
// briefly in the past during renewal-webhook lag.
const isSubscriptionInactive = (sub?: Subscription | null): boolean => {
  if (!sub) return true;
  if (sub.status === 'canceled') return true;
  if (sub.cancel_at_period_end) {
    const endMs = sub.end_at
      ? new Date(sub.end_at).getTime()
      : sub.current_period_end
        ? sub.current_period_end * 1000
        : null;
    if (endMs !== null && endMs < Date.now()) return true;
  }
  return false;
};

const ProtectedLayout = () => {
  const { isAuthenticated, masqueradeUser, currentUser, loadUserProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSubscriptionModalOpen = useAppSelectors.isSubscriptionModalOpen();
  const setIsSubscriptionModalOpen = useAppStore(
    (state) => state.setIsSubscriptionModalOpen,
  );
  const openCheckout = useAppStore((state) => state.openCheckout);
  const checkoutRequested = useAppStore((state) => state.checkoutRequested);
  const subscribeConversionFiredRef = useRef(false);

  // Stripe webhook may not have processed by the time the checkout redirect
  // lands, so suppress the paywall and poll until the subscription appears.
  const [awaitingCheckout, setAwaitingCheckout] = useState(
    () => new URLSearchParams(window.location.search).get('checkout_success') === '1',
  );
  const loadUserProfileRef = useRef(loadUserProfile);
  loadUserProfileRef.current = loadUserProfile;

  useEffect(() => {
    if (!awaitingCheckout) return;

    window.history.replaceState({}, '', window.location.pathname);

    // Google Ads (Percuity) Subscribe conversion. Stripe's checkout success_url
    // redirects a completed trial-start here (`/?checkout_success=1`), NOT to the
    // billing settings page — so this is the real "trial started" moment. Fire
    // once (the URL param is cleared above, so a reload won't re-fire).
    if (!subscribeConversionFiredRef.current) {
      subscribeConversionFiredRef.current = true;
      const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void })
        .gtag;
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', {
          send_to: 'AW-17529334203/BPnnCPiD6s0cELvT0aZB',
          value: 1.0,
          currency: 'USD',
        });
      }
    }

    let attempt = 0;
    const poll = setInterval(() => {
      attempt++;
      loadUserProfileRef.current(null, true);
      if (attempt >= 10) {
        clearInterval(poll);
        setAwaitingCheckout(false);
      }
    }, 1500);

    return () => clearInterval(poll);
  }, [awaitingCheckout]);

  useEffect(() => {
    if (awaitingCheckout && currentUser?.subscription) {
      setAwaitingCheckout(false);
    }
  }, [awaitingCheckout, currentUser?.subscription]);

  // Post-onboarding welcome modal — shown before subscription modal
  const isPostOnboarding =
    searchParams.get('openWelcomeBrief') === '1' ||
    searchParams.get('onboardingCompleted') === 'true';
  const [showWelcomeFirst, setShowWelcomeFirst] = useState(
    () => isPostOnboarding || localStorage.getItem('leo_welcome_pending') === '1',
  );
  const [postOnboardingHidden, setPostOnboardingHidden] = useState(isPostOnboarding);

  // Detect openWelcomeBrief param set after mount (e.g. demo intake flow)
  // Skip if user already has a subscription — the welcome modal won't render
  // and the subscription cleanup effect would immediately reset showWelcomeFirst,
  // causing an infinite setState loop.
  useEffect(() => {
    if (isPostOnboarding && !showWelcomeFirst && !currentUser?.subscription) {
      setShowWelcomeFirst(true);
      setPostOnboardingHidden(true);
    }
  }, [isPostOnboarding, showWelcomeFirst, currentUser?.subscription]);

  // Clean up onboarding search params and persist flag for post-Stripe redirect
  useEffect(() => {
    if (!isPostOnboarding) return;
    localStorage.setItem('leo_welcome_pending', '1');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openWelcomeBrief');
        next.delete('onboardingCompleted');
        return next;
      },
      { replace: true },
    );
  }, [isPostOnboarding, setSearchParams]);

  // Delayed reveal for post-onboarding transition
  useEffect(() => {
    if (!postOnboardingHidden) return;
    const timer = setTimeout(() => setPostOnboardingHidden(false), 2000);
    return () => clearTimeout(timer);
  }, [postOnboardingHidden]);

  // Clean up welcome pending flag when user returns from Stripe with a subscription.
  // The WelcomeModal won't render (user has subscription), so its onClose never fires.
  // Also clears the localStorage flag even if showWelcomeFirst was never flipped on
  // — the welcomePending render check would otherwise keep the modal suppressed for
  // a subscribed user (we gate shouldShowWelcome on !subscription, which prevents
  // visible regression, but the flag should still be cleaned up).
  useEffect(() => {
    if (currentUser?.subscription) {
      if (showWelcomeFirst) setShowWelcomeFirst(false);
      if (localStorage.getItem('leo_welcome_pending') === '1') {
        localStorage.removeItem('leo_welcome_pending');
      }
    }
  }, [showWelcomeFirst, currentUser?.subscription]);

  // Check subscription status and show modal if user is not subscribed
  // Defer subscription modal while welcome modal is still showing
  useEffect(() => {
    if (!isAuthenticated) {
      setIsSubscriptionModalOpen(false);
      return;
    }

    // Defer subscription modal while welcome flow or checkout polling is active.
    // Read localStorage every render — handleComplete sets the flag synchronously
    // before navigating, and that's the most reliable signal that we're mid-flow
    // (state and URL can lag a render behind on slow chunks / suspense boundaries).
    const welcomePending = localStorage.getItem('leo_welcome_pending') === '1';
    if (showWelcomeFirst || isPostOnboarding || welcomePending || awaitingCheckout) {
      setIsSubscriptionModalOpen(false);
      return;
    }

    // A deliberately opened modal isn't the paywall's to manage.
    if (checkoutRequested) {
      return;
    }

    if (
      currentUser &&
      isSubscriptionInactive(currentUser.subscription) &&
      // An internal operator masquerading in is servicing the client's account;
      // the client's billing state must not paywall the operator (mirrors the
      // backend subscription_required masquerade bypass).
      !masqueradeUser &&
      location.pathname !== '/onboarding' &&
      !location.pathname.startsWith('/onboarding') &&
      !location.pathname.startsWith('/auth')
    ) {
      setIsSubscriptionModalOpen(true);
    } else {
      setIsSubscriptionModalOpen(false);
    }
  }, [currentUser, masqueradeUser, isAuthenticated, location.pathname, showWelcomeFirst, isPostOnboarding, awaitingCheckout, checkoutRequested, setIsSubscriptionModalOpen]);

  // Listen for subscription-required events from API calls (e.g., chatbot)
  // This ensures the subscription modal is shown consistently across all features
  useEffect(() => {
    const handleSubscriptionRequired = () => {
      console.log('🔔 [ProtectedLayout] Received subscription-required event');
      openCheckout();
    };

    window.addEventListener('subscription-required', handleSubscriptionRequired);
    return () => {
      window.removeEventListener('subscription-required', handleSubscriptionRequired);
    };
  }, [openCheckout]);

  // Send toast indicating subscription is not current (active or trialing)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (
      currentUser &&
      currentUser.subscription &&
      currentUser.subscription.status !== 'active' &&
      currentUser.subscription.status !== 'trialing' &&
      // Don't yank a masquerading operator to billing — they're servicing a
      // (possibly billing-locked) client, not managing their own subscription.
      !masqueradeUser
    ) {
      navigate('/settings?tab=billing');
    }
  }, [currentUser, masqueradeUser, isAuthenticated, navigate]);

  // Check if this is an OAuth callback (allow it to complete even if onboarding is done)
  const isOAuthCallback =
    location.search.includes('auth_code') ||
    location.search.includes('code') ||
    location.search.includes('state') ||
    location.pathname === '/auth-callback';

  // User just landed from backend redirect after Google/federated sign-in; skip verification
  const isFederatedLoginSuccess =
    new URLSearchParams(location.search).get('federated_login_success') === '1';

  // User signed in but not yet verified -> send to signup verification step (step 3)
  const isVerified =
    currentUser?.confirmation_status === 'VERIFIED';
  if (
    currentUser &&
    !isVerified &&
    currentUser.confirmation_status !== 'FORCE_CHANGE_PASSWORD' &&
    !isOAuthCallback &&
    !isFederatedLoginSuccess
  ) {
    const email =
      (currentUser as { user_name?: string }).user_name ||
      currentUser.name ||
      '';
    const verifyUrl = email
      ? `/auth/signup?step=3&email=${encodeURIComponent(email)}`
      : '/auth/signup?step=3';
    return <Navigate to={verifyUrl} replace />;
  }

  // User signed in but never completed signup (e.g. missing company info) -> send to signup step 4
  const hasCompletedSignup =
    currentUser &&
    (currentUser.companyName || currentUser.companyDisplayName);

  if (
    currentUser &&
    !hasCompletedSignup &&
    !isOAuthCallback
  ) {
    return (
      <Navigate
        to="/auth/signup?step=4"
        replace
      />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Redirect users who have completed onboarding away from onboarding page
  // But allow OAuth callbacks to complete
  // Handle both /onboarding and /onboarding/ paths
  // Note: Workspace onboarding now uses a modal (WorkspaceOnboardingDrawer) instead of a route
  if (
    currentUser &&
    currentUser.onboard_completed &&
    location.pathname.startsWith('/onboarding') &&
    !isOAuthCallback
  ) {
    return <Navigate to="/" replace />;
  }

  if (
    currentUser &&
    (!currentUser.onboard_completed || !('onboard_completed' in currentUser)) &&
    location.pathname !== '/onboarding' &&
    !isOAuthCallback
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Special handling for onboarding page - render without sidebar/header
  if (location.pathname.startsWith('/onboarding')) {
    return (
      <>
        <Outlet />
        {masqueradeUser?.id && (
          <MasqueradeActiveSession user={masqueradeUser} />
        )}
      </>
    );
  }

  // Special handling for auth-callback page - render without sidebar/header
  if (location.pathname === '/auth-callback') {
    return (
      <>
        <Outlet />
        {masqueradeUser?.id && (
          <MasqueradeActiveSession user={masqueradeUser} />
        )}
      </>
    );
  }

  // Welcome modal wins as soon as we detect post-onboarding from any reliable
  // signal — URL param, in-flight state, or the localStorage flag that
  // handleComplete sets synchronously before navigating. Reading localStorage
  // each render is the safety net for cases where ProtectedLayout was already
  // mounted before the flag was set (e.g. the /onboarding auto-complete flow),
  // so the state-only gate would otherwise be stale on the first render after
  // navigate. Without this, the user briefly sees the subscription paywall
  // before the welcome modal catches up — exactly the regression we're fixing.
  const welcomePending =
    typeof window !== 'undefined' &&
    localStorage.getItem('leo_welcome_pending') === '1';
  const shouldShowWelcome =
    (showWelcomeFirst || isPostOnboarding || welcomePending) &&
    !currentUser?.subscription;

  // Render outlet for nested routes (MainAppLayout will be rendered here)
  return (
    <>
      <Outlet />
      {/* Post-onboarding: welcome modal first, then subscription modal */}
      {shouldShowWelcome && (
        <WelcomeModal
          onClose={() => {
            setShowWelcomeFirst(false);
            localStorage.removeItem('leo_welcome_pending');
            if (currentUser && !currentUser.subscription && !masqueradeUser) {
              setIsSubscriptionModalOpen(true);
            }
          }}
          hideContent={postOnboardingHidden}
          source="onboarding_redirect"
        />
      )}
      {!shouldShowWelcome && (
        <SubscriptionRequiredModal
          open={isSubscriptionModalOpen}
          onClose={() => {
            // Only allow closing if the subscription is active — mirrors the
            // paywall-open condition so a lapsed user can't dismiss it (which would
            // otherwise whack-a-mole with the reopen effect). A masquerading
            // operator opened it on purpose, so they may always dismiss it.
            if (masqueradeUser || !isSubscriptionInactive(currentUser?.subscription)) {
              setIsSubscriptionModalOpen(false);
            }
          }}
        />
      )}
    </>
  );
};

export default ProtectedLayout;
